import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TaskAltIcon from '@mui/icons-material/TaskAlt';

/**
 * One checklist row: state icon + title/description (or green receipt line when done)
 * + the expand action. The expanded panel is rendered as children.
 *
 * state: 'done' | 'next' | 'todo'
 */
function ChecklistStepRow({
  index,
  state,
  title,
  description,
  doneNote,
  expanded,
  onToggle,
  startLabel,
  closeLabel,
  children,
  'data-testid': dataTestId,
}) {
  const done = state === 'done';
  const next = state === 'next';

  return (
    <Box
      data-testid={dataTestId}
      data-step-state={state}
      sx={{
        borderTop: index === 0 ? 'none' : '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 1.75,
          py: 1.75,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        <Box sx={{ width: 28, flexShrink: 0, display: 'flex', justifyContent: 'center', pt: { xs: 0.25, sm: 0 } }}>
          {done ? (
            <CheckCircleIcon sx={{ fontSize: 26, color: 'success.main' }} />
          ) : (
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: 700,
                bgcolor: next ? 'secondary.main' : 'transparent',
                color: next ? '#fff' : 'text.disabled',
                border: next ? 'none' : '1.5px solid',
                borderColor: next ? 'transparent' : 'divider',
              }}
            >
              {index + 1}
            </Box>
          )}
        </Box>

        {/* minWidth on xs forces the action button onto its own row instead of
            squeezing the title into two or three words per line. */}
        <Box sx={{ flex: 1, minWidth: { xs: 200, sm: 0 } }}>
          <Typography
            sx={{
              fontSize: '15px',
              fontWeight: 600,
              lineHeight: 1.35,
              color: done ? 'text.secondary' : 'text.primary',
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.625,
              mt: 0.25,
              fontSize: '13px',
              color: done ? 'success.main' : 'text.secondary',
            }}
          >
            {done && <TaskAltIcon sx={{ fontSize: 14, flexShrink: 0 }} />}
            <Typography component="span" sx={{ fontSize: '13px', color: 'inherit', minWidth: 0 }}>
              {done ? doneNote : description}
            </Typography>
          </Box>
        </Box>

        {!done && (
          <Box sx={{ flexShrink: 0, ml: { xs: 5.5, sm: 0 } }}>
            <Button
              size="small"
              variant={expanded ? 'text' : next ? 'contained' : 'outlined'}
              color={expanded ? 'inherit' : 'secondary'}
              endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={onToggle}
              data-testid={dataTestId ? `${dataTestId}-toggle` : undefined}
            >
              {expanded ? closeLabel : startLabel}
            </Button>
          </Box>
        )}
      </Box>

      {expanded && <Box sx={{ pb: 2.25, pl: { xs: 0, sm: 5.25 }, pt: 0.25 }}>{children}</Box>}
    </Box>
  );
}

export default ChecklistStepRow;
